import { useIsMutating } from "@tanstack/react-query";
import { Link, Outlet, useRouter } from "@tanstack/react-router";
import { type ReactNode, Suspense, useEffect } from "react";
import { ApiError } from "@/shared/api/client";
import { t, useLocale } from "@/shared/lib/i18n";
import { Button, Spinner } from "@/shared/ui/primitives/Button";

/**
 * The outermost frame.
 *
 * Three jobs only: a skip link for keyboard users, a suspense boundary for the
 * lazily loaded routes, and a guard against closing the tab on top of a write
 * that has not landed. Everything that looks like the application lives further
 * down, inside the authenticated shell.
 */
export function RootLayout() {
  useLocale();
  useUnsavedWorkGuard();

  return (
    <>
      {/* `tabIndex` is not redundant here. Safari leaves links out of the tab
          order unless the person has turned on "Press Tab to highlight each
          item", so without it the skip link — the one control that exists
          purely for keyboard users — is the one they cannot reach. An explicit
          tabindex puts it back. */}
      <a
        href="#main"
        tabIndex={0}
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-surface-raised focus:px-3 focus:py-2 focus:text-sm focus:shadow-[var(--ex-shadow-overlay)]"
      >
        {" "}
        {t("copy.bc92d677c3c6")}{" "}
      </a>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </>
  );
}

/**
 * The loading state between route chunks.
 *
 * Held back for a moment before appearing: a spinner that flashes for 80ms is
 * more disruptive than no spinner at all, and most chunks arrive faster than
 * that.
 */
function RouteFallback() {
  useLocale();
  return (
    <main id="main" className="flex h-dvh items-center justify-center" aria-busy="true">
      <div className="animate-[fade-in_200ms_ease-out_120ms_both] text-ink-faint">
        <Spinner className="size-5" />
      </div>
    </main>
  );
}

/**
 * Where a thrown render lands.
 *
 * Three things matter here and only three: say plainly that something broke,
 * offer a way back into the product, and never show a stack trace. A person
 * cannot act on `Cannot read properties of undefined`, and a server error
 * message can leak more than it helps.
 *
 * The details are logged to the console, where a developer can find them,
 * rather than rendered, where they only frighten people.
 */
export function RouteError({ error, reset }: { error: Error; reset?: () => void }) {
  useLocale();
  const router = useRouter();
  const documentTitle = t("copy.b2b27ec9d43c");

  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  // Network trouble and a genuine bug deserve different sentences, because they
  // deserve different actions.
  const offline = error instanceof ApiError && error.status === 0;
  const server = error instanceof ApiError && error.status >= 500;

  // Never put an exception object in telemetry/console wholesale: response
  // details and validation values can contain private user input. The bounded
  // code and correlation id are enough to join this report to server logs.
  console.error(
    "Beklenmeyen hata:",
    error instanceof ApiError
      ? { status: error.status, code: error.code, requestId: error.requestId }
      : { name: error.name },
  );

  return (
    <main
      id="main"
      tabIndex={-1}
      className="flex h-dvh items-center justify-center px-6 outline-none"
    >
      <div className="w-full max-w-md">
        <RouteState
          title={
            offline
              ? t("copy.fd2dd2868fa0")
              : server
                ? t("copy.7425beb94bea")
                : t("copy.984e2985dfba")
          }
          description={
            <>
              {offline
                ? t("copy.84a6a839fa04")
                : server
                  ? t("copy.e2a5508d620d")
                  : t("copy.a07db22fd3a9")}
              {server && error.requestId ? (
                <span className="mt-2 block font-mono text-xs" data-testid="request-reference">
                  {t("activity.request", { id: error.requestId })}
                </span>
              ) : null}
            </>
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  reset?.();
                  void router.invalidate();
                }}
              >
                {" "}
                {t("copy.0ce9380b07f8")}{" "}
              </Button>
              <Link
                to="/planlayici"
                search={{}}
                className="text-sm font-medium text-accent-ink underline underline-offset-4"
              >
                {" "}
                {t("copy.261b635999f7")}{" "}
              </Link>
            </div>
          }
        />
      </div>
    </main>
  );
}

export function NotFound() {
  useLocale();
  const documentTitle = t("copy.239465761503");
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  return (
    <main
      id="main"
      tabIndex={-1}
      className="flex h-dvh items-center justify-center px-6 outline-none"
    >
      <div className="w-full max-w-md">
        <RouteState
          title={t("copy.bbed3440afef")}
          description={t("copy.995ef3984833")}
          action={
            <Link
              to="/planlayici"
              className="text-sm font-medium text-accent-ink underline underline-offset-4"
            >
              {" "}
              {t("copy.261b635999f7")}{" "}
            </Link>
          }
        />
      </div>
    </main>
  );
}

function RouteState({
  title,
  description,
  action,
}: {
  title: string;
  description: ReactNode;
  action: ReactNode;
}) {
  useLocale();
  return (
    <div className="flex flex-col items-start gap-3 py-10">
      <div className="max-w-md">
        <h1 className="text-sm font-medium text-ink">{title}</h1>
        <p className="mt-1 text-sm leading-relaxed text-ink-faint">{description}</p>
      </div>
      {action}
    </div>
  );
}

/**
 * Do not let a write die on the way out.
 *
 * Almost everything here saves without a save button — the note editor writes
 * on a 700ms debounce, editors commit on blur — so the window between "typed"
 * and "stored" is real and invisible. Closing the tab or reloading inside that
 * window silently discarded the work: the request is aborted with the document
 * and nothing anywhere said so.
 *
 * Registered only while something is genuinely in flight, so it can never
 * become the dialog that appears every time you leave. Client-side navigation
 * is unaffected — a fetch survives a route change, and interrupting a person
 * moving around their own app would be a worse bug than the one this fixes.
 */
function useUnsavedWorkGuard() {
  const pending = useIsMutating();

  useEffect(() => {
    if (pending === 0) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [pending]);
}
